import React from "react";
import {
  Box,
  Flex,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  Spinner,
  VStack,
  Text,
} from "@chakra-ui/react";
import PageLayoutWithHeaderNav from "./PageLayoutWithHeaderNav";
import OrganizationManagement from "./admin/OrganizationManagement";
import UserManagement from "./admin/UserManagement";
import SignatureManagement from "./admin/SignatureManagement";
import OTAManagement from "./admin/OTAManagement";
import DeviceManagement from "./admin/DeviceManagement";
import SessionManagement from "./admin/SessionManagement";
import AudioBatchManagement from "./admin/AudioBatchManagement";
import ThreadManagement from "./admin/ThreadManagement";
import ConversationLoader from "./admin/ConversationLoader";
import { useUser } from "../contexts/UserContext";
import { useSearchParams } from "react-router-dom";

const AdminPage = () => {
  const { loading, error, isAdmin } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const TAB_DEFS = [
    { id: "organizations", label: "Organizations" },
    { id: "users", label: "Users" },
    { id: "threads", label: "Threads" },
    { id: "signatures", label: "Signatures" },
    { id: "devices", label: "Devices" },
    { id: "sessions", label: "Sessions" },
    { id: "conversation-loader", label: "User Bulk Upload" },
    { id: "ota-firmware", label: "OTA Firmware" },
    { id: "audio-batches", label: "Audio Batches" },
  ];

  const tabIdFromUrl = (searchParams.get("tab") || "").toLowerCase();
  const selectedTabIndex = Math.max(
    0,
    TAB_DEFS.findIndex((t) => t.id === tabIdFromUrl)
  );

  const handleTabChange = (nextIndex) => {
    const nextTab = TAB_DEFS[nextIndex]?.id || TAB_DEFS[0].id;
    const next = new URLSearchParams(searchParams);
    next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };

  if (loading) {
    return (
      <PageLayoutWithHeaderNav>
        <Flex justify="center" align="center" minH="40vh" direction="column" gap={4}>
          <Spinner size="xl" thickness="3px" color="blue.500" />
          <Text color="gray.500" fontSize="sm">Loading Admin Panel...</Text>
        </Flex>
      </PageLayoutWithHeaderNav>
    );
  }

  if (error) {
    return (
      <PageLayoutWithHeaderNav>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {error?.message || "Failed to load user information"}
        </Alert>
      </PageLayoutWithHeaderNav>
    );
  }

  if (!isAdmin) {
    return (
      <PageLayoutWithHeaderNav>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          Access denied. Admin privileges required.
        </Alert>
      </PageLayoutWithHeaderNav>
    );
  }

  return (
    <PageLayoutWithHeaderNav>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box
          px={6}
          py={5}
          bg="white"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="sm"
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="lg" letterSpacing="-0.5px">
                Admin Panel
              </Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Manage users, devices, and platform configuration
              </Text>
            </Box>
          </Flex>
        </Box>

        {/* Tabs */}
        <Tabs
          variant="line"
          colorScheme="blue"
          index={selectedTabIndex}
          onChange={handleTabChange}
        >
          <Box
            bg="white"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="sm"
            overflow="hidden"
          >
            <TabList
              px={4}
              borderBottomWidth="1px"
              borderBottomColor="gray.100"
              bg="gray.50"
            >
              {/* Old labels (kept for reference): */}
              {/* {["Organizations", "Users", "Signatures", "Devices", "Sessions", "OTA Firmware", "Audio Batches"].map(...) } */}
              {TAB_DEFS.map((t) => (
                  <Tab
                    key={t.id}
                    fontSize="sm"
                    fontWeight="medium"
                    py={4}
                    px={5}
                    color="gray.500"
                    _selected={{
                      color: "blue.600",
                      borderBottomColor: "blue.500",
                      borderBottomWidth: "2px",
                      fontWeight: "semibold",
                      bg: "transparent",
                    }}
                    _hover={{ color: "gray.700" }}
                  >
                    {t.label}
                  </Tab>
                ))}
            </TabList>

            <TabPanels>
              <TabPanel p={6}><OrganizationManagement /></TabPanel>
              <TabPanel p={6}><UserManagement /></TabPanel>
              <TabPanel p={6}><ThreadManagement /></TabPanel>
              <TabPanel p={6}><SignatureManagement /></TabPanel>
              <TabPanel p={6}><DeviceManagement /></TabPanel>
              <TabPanel p={6}><SessionManagement /></TabPanel>
              <TabPanel p={6}><ConversationLoader /></TabPanel>
              <TabPanel p={6}><OTAManagement /></TabPanel>
              <TabPanel p={6}><AudioBatchManagement /></TabPanel>
            </TabPanels>
          </Box>
        </Tabs>
      </VStack>
    </PageLayoutWithHeaderNav>
  );
};

export default AdminPage;
